import React, { useEffect, useState } from 'react';
import VersionSelect from '@/components/_common/VersionSelect';
import PolarisIcon from '@/components/_common/PolarisIcon';
import { selectInventoryIsLoading, selectOutputVersionOptions, selectVersionKeys, selectFilters, selectSearchValue } from '@/redux/selectors/orderPlacementSelectors';
import { getOutputVersionOptions, getTitleInventory, setSelectedVersionId, setFilters } from '@/redux/slices/orderPlacementSlice';
import { selectActiveTitle, selectActiveTitleId, selectRootTitle, selectIsActiveTitleLoading } from '@/redux/selectors/titleV2Selectors';
import { setActiveTitleId } from '@/redux/slices/titleV2Slice';
import { Button, Fade, Grid } from '@mui/material';
import Spinner from '../../_common/Spinner';
import OPInventory from './OPInventory';
import TitleBrowser from './TitleBrowser/TitleBrowser';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import type { TitleEntity } from '@/redux/slices/slice_types';
import type { OPViewingInventoryProps } from '@/components/OrderPlacement/OPStep2FileSelection/OPViewingInventory.types';
import { useConfigValue } from '@/components/_hooks/useConfigValue';

/**
 * Returns the initial (default) version to be displayed in the inventory.
 * @param versions
 * @param initialVersionId id of the version to be found in the versions' array.
 *  If not found, the first version in an array will be returned.
 */
const getInitialVersion = (versions: TitleEntity[], initialVersionId?: number): TitleEntity | undefined => {
  let initialVersion: TitleEntity | undefined;

  if (typeof initialVersionId === 'number') {
    initialVersion = versions.find((v) => v.id === initialVersionId);
  }

  if (!initialVersion) {
    initialVersion = versions[0];
  }

  return initialVersion;
};

/**
 * The difference between `OPViewingInventory` and `OPViewingInventoryV2` is that OPViewingInventoryV2:
 *  * relies on titles received from titles V2 API
 *  * uses titles V2 slice and selectors
 */
const OPViewingInventoryV2: React.FC<OPViewingInventoryProps> = ({ isPackagingInventory = false, initialVersionId, fileType, onClickFile }) => {
  const dispatch = useAppDispatch();
  const rootTitle = useAppSelector(selectRootTitle);
  const activeTitleId = useAppSelector(selectActiveTitleId);
  const activeTitle = useAppSelector(selectActiveTitle);
  const outputVersionOptions = useAppSelector(selectOutputVersionOptions);
  const versionKeys = useAppSelector(selectVersionKeys);
  const activeTitleChildren = activeTitle?.relationships.children;
  const hasVersions = Boolean(activeTitleChildren?.find((c) => c.entity_type === 'version')) || activeTitle?.entity_type === 'episode';
  const loading = useAppSelector(selectIsActiveTitleLoading);
  const filters = useAppSelector(selectFilters);
  const titleSearchValue = useAppSelector(selectSearchValue);

  const loadingInventory = useAppSelector(selectInventoryIsLoading);
  const [isLoading, setIsLoading] = useState(true);
  const [hasLoadedFlag, setHasLoadedFlag] = useState(false);

  const versions = activeTitleId !== null ? outputVersionOptions[activeTitleId] || [] : [];
  const skipRallyIdCheck = useConfigValue(['orderPlacementConfig', 'skipRallyIdCheck']);
  const calculateInitialVersion = useConfigValue(['orderPlacementConfig', 'calculateInitialVersion']);
  const existingVersions = skipRallyIdCheck === true ? versions : versions.filter((v) => Boolean(v.rally_id));
  const dropdownOptions = existingVersions.map((v) => v.id);
  // TODO: In this component, `selectedVersionId` is derived from `filters.version_name_filter`.
  //  I believe it should be the other way around — the selected version should determine the filters, not vice versa.
  //  If you disagree, please remove this comment and replace it with one that explains why this approach is preferred.
  const selectedVersionId = existingVersions.find((v) => v.name === filters.version_name_filter?.[0])?.id || null;

  useEffect(() => {
    return () => {
      dispatch(setSelectedVersionId(null));
    };
  }, []);

  useEffect(() => {
    if (loadingInventory) {
      setHasLoadedFlag(true);
    }

    if (hasLoadedFlag && !loadingInventory) {
      setIsLoading(false);
    }
  }, [loadingInventory]);

  useEffect(() => {
    setIsLoading(true);
    setHasLoadedFlag(false);
  }, [rootTitle]);

  useEffect(() => {
    const abort = loadInventory();
    return abort;
  }, [filters]);

  useEffect(() => {
    dispatch(setSelectedVersionId(selectedVersionId));
  }, [selectedVersionId]);

  const loadInventory = () => {
    if (!filters.version_name_filter?.length) return;
    const promise = dispatch(getTitleInventory({ id: activeTitleId, filters }));
    return () => promise.abort();
  };

  useEffect(() => {
    if (activeTitleId !== null && hasVersions) {
      /**
       * `getVersionsByTitleId` function gets the versions from the redux store if they were already loaded.
       * Otherwise, it will dispatch a request to the API to get the versions.
       */
      const getVersionsByTitleId: () => Promise<TitleEntity[]> = outputVersionOptions[activeTitleId]
        ? () => {
          setIsLoading(false);
          return Promise.resolve(outputVersionOptions[activeTitleId]);
        }
        : () => dispatch(getOutputVersionOptions(activeTitleId)).unwrap();

      getVersionsByTitleId().then((resolvedVersions) => {
        const existingVersions = resolvedVersions.filter((v) => skipRallyIdCheck || Boolean(v.rally_id));
        // TODO(PBS-733): after users change the title, `initialVersionId` should be invalidated and should be used anymore. It's relevant only when OPViewingInventoryV2 initially opened.
        const initialVersion = getInitialVersion(existingVersions, initialVersionId);
        if (calculateInitialVersion) {
          const versionName = calculateInitialVersion(existingVersions, titleSearchValue);
          if (versionName) {
            dispatch(setFilters({ ...filters, version_name_filter: [versionName] }));
          } else {
            setIsLoading(false);
          }
        } else if (initialVersion) {
          dispatch(setFilters({ ...filters, version_name_filter: [initialVersion.name] }));
        } else {
          // if there is no `initialVersion` and dispatch(setFilters({ ... }) is not called, `filters` won't change and `loadInventory()` is not going to be called.
          //  So we need to call `setIsLoading(false)`, otherwise <OPInventory /> will always show a spinner.
          setIsLoading(false);
        }
      });
    }
  }, [activeTitleId, titleSearchValue, calculateInitialVersion, hasVersions, outputVersionOptions]);

  const selectVersion = (e: { target: { value: string } }) => {
    const value = e.target.value;
    dispatch(
      setFilters({
        ...filters,
        version_name_filter: [versionKeys[value]?.name]
      })
    );
  };

  const goBack = () => {
    if (!rootTitle) {
      throw new Error('rootTitle is not loaded yet');
    }

    dispatch(setActiveTitleId(rootTitle?.id));
  };

  if (loading) return <Spinner contained />;

  return (
    <div>
      {!hasVersions ? (
        <TitleBrowser />
      ) : (
        <Fade in timeout={500}>
          <div>
            <Grid container justifyContent="space-between" alignItems="center">
              {rootTitle && activeTitleId !== rootTitle.id && (
                <Button variant="contained" color="primary" onClick={goBack} startIcon={<PolarisIcon icon="back" />}>
                  Back to title
                </Button>
              )}
              <VersionSelect
                label="Version"
                options={dropdownOptions}
                value={selectedVersionId}
                onChange={selectVersion}
                sx={{ ml: 'auto', minWidth: 320 }}
                disabled={isLoading || dropdownOptions.length === 0}
              />
            </Grid>
            <OPInventory onClickFile={onClickFile} fileType={fileType} loadInventory={loadInventory} loading={isLoading} isPackagingInventory={isPackagingInventory} />
          </div>
        </Fade>
      )}
    </div>
  );
};

export default OPViewingInventoryV2;
